import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  hiringLinksAPI,
  type HiringAttentionChoice,
  type HiringAttentionItem,
  type HiringAttentionResult,
  type HiringLink,
} from '../../services/api';
import {
  ATTENTION_WORDS, HIRING_CODES, attentionAnswered, attentionBox, hiringCodeOf, hiringErrorText, isHiringOff,
} from './hiringText';

/**
 * The questions a message from Recruitment left on one link, as the hiring panel asks
 * them: the address changed, the positions changed, or the opening was deleted or closed
 * there. Each box says what changed, who Recruitment recorded as having changed it and
 * when, and offers what can be done.
 *
 * Nothing here is sent to Recruitment. "Still this store" and "Keep following" accept
 * what Recruitment now has; "Close" ends ChannelPlex's link. "Raise again" also only
 * closes the link - the owner then opens the existing Raise hiring window, and Raise does
 * the rest by its own rules. "Stop following" is the link's own stop, unchanged: the
 * card asks its usual question before anything is stopped.
 *
 * An answer brings back the token its question came with. When Recruitment changed the
 * opening again while the person was reading, the server refuses the answer and says so;
 * the owner's list is then read again, so the box shows the latest and can be answered.
 *
 * An answer refused because the link is no longer followed (someone else stopped,
 * cancelled or closed it first) is the card's to show (`onLinkGone`), exactly as it shows
 * one that "Stop following" found already stopped: the owner's re-read drops the link, and
 * the card must not go with it.
 *
 * A person without Hiring (manage) sees the boxes with no buttons, and a line that says
 * who can answer. Employee codes only: the API sends no names, and none is drawn.
 */

const BOX = 'space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900';
const BUTTON = 'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';

export function HiringAttentionBox({
  projectId, link, canManage, onAnswered, onStop, onRaiseAgain, onOutOfDate, onLinkGone, onHiringGone,
}: {
  projectId: number;
  link: HiringLink;
  canManage: boolean;
  /** An answer went through: the link as the server just answered it. */
  onAnswered: (result: HiringAttentionResult) => void;
  /** "Stop following" was pressed: the card asks its own question. Without it the button is not drawn. */
  onStop?: () => void;
  /**
   * Opens Raise hiring for this link's store or role, once the link has been closed for
   * it. Given only where the owner has such a window to open; without it "Raise again"
   * is not drawn.
   */
  onRaiseAgain?: () => void;
  /** The owner's list no longer shows this link as it is: it is to be read again. */
  onOutOfDate: () => void;
  /**
   * The link is no longer followed (someone stopped, cancelled or closed it first). The
   * card says so, in `sentence`, and keeps itself on show. Where it is not given, the box
   * says the sentence itself and the list is read again (`onOutOfDate`).
   */
  onLinkGone?: (sentence: string) => void;
  /** Hiring was switched off for the project while the panel was open. */
  onHiringGone: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // What the last answer did, said once it went through: the question itself has gone by then.
  const [done, setDone] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const items = link.state === 'live' ? link.attentionItems ?? [] : [];

  const answer = async (item: HiringAttentionItem, choice: HiringAttentionChoice) => {
    if (busy) return;
    setBusy(`${item.reason}:${choice}`);
    setError(null);
    setDone(null);
    try {
      const res = await hiringLinksAPI.answerAttention(projectId, link.id, { reason: item.reason, choice, token: item.token });
      const result = res?.data?.data;
      if (!result?.link) throw new Error('unreadable answer');
      // The owner is told whether or not this box is still on show.
      onAnswered(result);
      if (!alive.current) return;
      setDone(attentionAnswered(item.reason, result.result, result.link));
      // Closed to raise again: the owner closes this panel and opens Raise hiring.
      if (result.result === 'closed' && result.raiseAgain) onRaiseAgain?.();
    } catch (err) {
      if (isHiringOff(err)) {
        onHiringGone();
        return;
      }
      // Someone else ended the link first: the card is the one to say so, and to stay on show.
      if (onLinkGone && hiringCodeOf(err) === HIRING_CODES.linkNotLive) {
        onLinkGone(hiringErrorText(err, ATTENTION_WORDS.answerFailed));
        return;
      }
      // Whatever else the refusal, the list on the page may be behind: changed again, answered
      // by someone else. It is read again, and the box then shows what is true now.
      onOutOfDate();
      if (alive.current) setError(hiringErrorText(err, ATTENTION_WORDS.answerFailed));
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  if (items.length === 0 && !done && !error) return null;

  return (
    <div className="space-y-2">
      {done && <p role="status" className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">{done}</p>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</p>}
      {items.map((item) => {
        const box = attentionBox(item, link, { canManage, canRaiseAgain: !!onRaiseAgain });
        const stop = box.stop && !!onStop;
        return (
          <div key={item.reason} role="group" aria-label={box.title} className={BOX}>
            <p className="font-semibold">{box.title}</p>
            {box.lines.map((line) => <p key={line}>{line}</p>)}
            {box.question && <p className="font-medium">{box.question}</p>}
            {(box.buttons.length > 0 || stop) && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                {box.buttons.map((button) => (
                  <button
                    key={button.choice}
                    type="button"
                    disabled={busy !== null}
                    // "Close" here ends the hiring link; the panel's own "Close" only shuts the window.
                    aria-label={button.choice === 'close' ? ATTENTION_WORDS.closeName : undefined}
                    onClick={() => void answer(item, button.choice)}
                    className={`${BUTTON} ${button.primary
                      ? 'bg-amber-700 text-white hover:bg-amber-800'
                      : 'border border-amber-300 bg-white text-amber-900 hover:bg-amber-100'}`}
                  >
                    {busy === `${item.reason}:${button.choice}` && <Loader2 className="h-3 w-3 animate-spin" />}
                    {button.label}
                  </button>
                ))}
                {stop && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={onStop}
                    className={`${BUTTON} border border-amber-300 bg-white text-amber-900 hover:bg-amber-100`}
                  >
                    {ATTENTION_WORDS.stopFollowing}
                  </button>
                )}
              </div>
            )}
            {box.footnote && <p className="text-amber-800">{box.footnote}</p>}
            {!canManage && <p className="text-amber-800">{ATTENTION_WORDS.cannotAnswer}</p>}
          </div>
        );
      })}
    </div>
  );
}

export default HiringAttentionBox;
